import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import S3Image from "../common/S3Image";
import {
  BellIcon,
  Bars3Icon,
  UserIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { logout, selectCurrentUser } from "../../store/slices/authSlice";
import { persistor } from "../../store";
import NotificationDropdown from "./NotificationDropdown";
import {
  useGetUnreadPostsQuery,
  useGetUnreadEventsQuery,
  useGetUnreadTasksQuery,
  useGetCurrentTenantQuery,
} from "../../shared";

const TopBar = ({ onMobileMenuClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Fetch unread notifications count
  const { data: unreadPostsData } = useGetUnreadPostsQuery();
  const { data: unreadEventsData } = useGetUnreadEventsQuery();
  const { data: unreadTasksData } = useGetUnreadTasksQuery();
  const { data: tenantData } = useGetCurrentTenantQuery();

  const unreadCount =
    (unreadPostsData?.data?.count || 0) +
    (unreadEventsData?.data?.count || 0) +
    (unreadTasksData?.data?.count || 0);
  const hasUnread = unreadCount > 0;

  // Memoize user display values to prevent unnecessary re-renders
  const userName = useMemo(() => {
    if (!user?.firstName || !user?.lastName) return "";
    return `${user.firstName} ${user.lastName}`;
  }, [user?.firstName, user?.lastName]);

  const userProfileImage = useMemo(() => {
    return user?.profileImageUrl || "";
  }, [user?.profileImageUrl]);

  const handleLogout = () => {
    // Clear Redux auth state
    dispatch(logout());

    // Purge persisted state from localStorage
    persistor.purge();

    // Navigate to login
    navigate("/login");
  };

  const userMenuItems = [
    {
      label: "View Profile",
      icon: UserIcon,
      onClick: () => navigate("/dashboard/profile"),
    },
    {
      label: "Logout",
      icon: ArrowRightOnRectangleIcon,
      onClick: handleLogout,
      className: "text-error-600",
    },
  ];

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-neutral-200 shadow-sm">
      <div className="relative flex items-center justify-between px-4 py-3">
        {/* Left: Mobile menu + Logo */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <button
            onClick={onMobileMenuClick}
            className="md:hidden p-2 rounded-lg hover:bg-neutral-100"
            aria-label="Open menu"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Logo (mobile only) */}
          <div className="md:hidden">
            <span className="text-xl font-bold text-primary-700">
              Battalion Hub
            </span>
          </div>
        </div>

        {/* Center: Org name + logo (desktop only) */}
        {tenantData?.data && (
          <div className="hidden md:flex items-center gap-3 absolute left-1/2 -translate-x-1/2">
            {tenantData.data.logoUrl && (
              <S3Image
                src={tenantData.data.logoUrl}
                alt={tenantData.data.name}
                variant="image"
                className="h-8 w-auto object-contain"
              />
            )}
            <span className="text-base font-semibold text-neutral-800">
              {tenantData.data.name}
            </span>
          </div>
        )}

        {/* Right: Notifications + User Menu */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative p-2 rounded-lg hover:bg-neutral-100"
              aria-label="Notifications"
            >
              <BellIcon className="w-6 h-6" />
              {/* Red dot badge - only show when there are unread notifications */}
              {hasUnread && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent-600 rounded-full" />
              )}
            </button>

            {notificationsOpen && (
              <NotificationDropdown
                onClose={() => setNotificationsOpen(false)}
              />
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-1 rounded-lg hover:bg-neutral-100"
              aria-label="User menu"
            >
              <S3Image
                src={userProfileImage}
                alt={userName || "User"}
                name={userName}
                size="sm"
              />
              <span className="hidden md:block text-sm font-medium">
                {user?.firstName}
              </span>
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />

                {/* Menu */}
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-elevated border border-neutral-200 z-50">
                  <div className="p-4 border-b border-neutral-200">
                    <p className="font-semibold">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-sm text-neutral-600">{user?.email}</p>
                  </div>
                  <div className="py-2">
                    {userMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          item.onClick();
                          setUserMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-neutral-50 transition-colors ${
                          item.className || ""
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
